// ─────────────────────────────────────────────────────────────────────────────
// agenda.tsx — Forum (c01) corporate variant: ANNUAL & GALA
//
// Reference language: Stripe Sessions, Figma Config, ReactConf, Apple Events.
// Confident, tabular, monospace-anchored. The accent color is intentionally
// the same as the ink — strict monochrome on cream paper. The "color" comes
// from rhythm: numbered sections, hairline rules, tnum figures, and the
// terminal-window speaker cards.
//
// Owned-file rules: this variant must compile against VariantProps unchanged.
// We do not import anything outside the allowed surface.
//
// Signature element (item 10 in the brief):
//   On enter, the hero subtitle types out character-by-character (~30ms/char)
//   with a blinking caret. After the type completes the caret stops blinking
//   and the rest of the page settles. `prefers-reduced-motion: reduce` shows
//   the full subtitle instantly with no caret animation.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  GuestPhoto,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ─── Reduced-motion helper ──────────────────────────────────────────────────
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduce(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduce;
}

// ─── IntersectionObserver-based reveal (snappy: 200ms, 4px) ─────────────────
function useReveal<T extends HTMLElement>(threshold = 0.15) {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        }
      },
      { threshold, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return { ref, shown };
}

// ─── Live-typing terminal effect for the hero subtitle ──────────────────────
function useTypewriter(full: string, speedMs = 30, enabled = true) {
  const [out, setOut] = useState(enabled ? "" : full);
  const [done, setDone] = useState(!enabled);
  useEffect(() => {
    if (!enabled) {
      setOut(full);
      setDone(true);
      return;
    }
    setOut("");
    setDone(false);
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setOut(full.slice(0, i));
      if (i >= full.length) {
        window.clearInterval(id);
        setDone(true);
      }
    }, speedMs);
    return () => window.clearInterval(id);
  }, [full, speedMs, enabled]);
  return { out, done };
}

// ─── Animated dot-grid backdrop with scan gradient ──────────────────────────
//
// A pure-CSS hero backdrop. Two stacked layers:
//   1. radial-gradient dot lattice (8px) — static schematic feel.
//   2. linear sweep — slowly slides Y on a 14s loop. Disabled if reduce-motion.
//
// We inject a scoped <style> for the keyframes so we don't touch globals.
function HeroBackdrop({ ink, animate }: { ink: string; animate: boolean }) {
  const styleId = "forum-hero-bg-anim";
  return (
    <>
      <style>{`
        @keyframes forum-scan {
          0%   { transform: translateY(-30%); opacity: 0.0; }
          12%  { opacity: 0.55; }
          50%  { opacity: 0.55; }
          88%  { opacity: 0.0; }
          100% { transform: translateY(130%); opacity: 0; }
        }
        @keyframes forum-caret {
          0%, 49%   { opacity: 1; }
          50%, 100% { opacity: 0; }
        }
        @keyframes forum-ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @keyframes forum-bar {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .forum-scan, .forum-caret, .forum-ticker, .forum-bar {
            animation: none !important;
          }
        }
      `}</style>
      <div
        id={styleId}
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          backgroundImage: `radial-gradient(circle, ${ink}1A 1px, transparent 1px)`,
          backgroundSize: "18px 18px",
          backgroundPosition: "0 0",
          maskImage: "radial-gradient(ellipse at 50% 40%, #000 0%, #000 60%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse at 50% 40%, #000 0%, #000 60%, transparent 100%)",
        }}
      />
      <div
        aria-hidden
        className="forum-scan"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: `linear-gradient(180deg, transparent 0%, ${ink}10 45%, ${ink}1A 50%, ${ink}10 55%, transparent 100%)`,
          mixBlendMode: "multiply",
          animation: animate ? "forum-scan 14s linear infinite" : undefined,
        }}
      />
    </>
  );
}

// ─── Section heading: monospace `// 0N — TITLE` ─────────────────────────────
function SectionLabel({ n, label, ink, font }: { n: string; label: string; ink: string; font: string }) {
  return (
    <div
      className="forum-section-label"
      style={{
        fontFamily: font,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.3em",
        color: ink,
        marginBottom: 32,
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontFeatureSettings: '"tnum" 1, "lnum" 1',
      }}
    >
      <span>// {n}</span>
      <span aria-hidden style={{ width: 24, height: 1, background: ink }} />
      <span>{label}</span>
    </div>
  );
}

// ─── Reveal wrapper: 200ms, 4px translate, opacity ──────────────────────────
function Reveal({
  children,
  reduce,
  delay = 0,
  as: Tag = "div",
  style,
  className,
}: {
  children: React.ReactNode;
  reduce: boolean;
  delay?: number;
  as?: "div" | "section";
  style?: CSSProperties;
  className?: string;
}) {
  const { ref, shown } = useReveal<HTMLDivElement>();
  const visible = reduce || shown;
  const merged: CSSProperties = {
    ...style,
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(4px)",
    transition: reduce
      ? "none"
      : `opacity 200ms ease ${delay}ms, transform 200ms ease ${delay}ms`,
    willChange: reduce ? undefined : "opacity, transform",
  };
  // We only support div/section here — keep prop wiring simple.
  if (Tag === "section") {
    return (
      <section ref={ref as React.RefObject<HTMLElement> as React.RefObject<HTMLDivElement>} className={className} style={merged}>
        {children}
      </section>
    );
  }
  return (
    <div ref={ref} className={className} style={merged}>
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// VariantAgenda — corporate annual gala
// ═══════════════════════════════════════════════════════════════════════════
export function VariantAgenda({ t, isDark, ctx }: VariantProps) {
  const reduce = usePrefersReducedMotion();
  const { play } = useSound();
  const tt = useT();

  // Hero name split — graceful fallback if no " & " separator.
  const [nameLeft, nameRight] = useMemo(() => {
    const parts = t.names.split(" & ");
    return parts.length === 2 ? parts : [t.names, ""];
  }, [t.names]);

  // Live-typing subtitle — see header comment, item 10.
  const subtitleFull = `> ${t.venue.toUpperCase()} · ${tt("variant.c01.hero.venue_suffix")} · ${t.date} · 19:00`;
  const { out: typed, done: typedDone } = useTypewriter(subtitleFull, 28, !reduce);

  const tnum: CSSProperties = { fontFeatureSettings: '"tnum" 1, "lnum" 1' };

  // Tabular agenda rows — start time + duration in minutes (for the bars).
  const agendaRows: Array<{ time: string; mins: number; title: string; sub: string; detail: string }> = [
    { time: "19:00", mins: 45, title: tt("variant.c01.agenda.0.title"), sub: tt("variant.c01.agenda.0.sub"), detail: tt("variant.c01.agenda.0.detail") },
    { time: "19:45", mins: 45, title: tt("variant.c01.agenda.1.title"), sub: tt("variant.c01.agenda.1.sub"), detail: tt("variant.c01.agenda.1.detail") },
    { time: "20:30", mins: 60, title: tt("variant.c01.agenda.2.title"), sub: tt("variant.c01.agenda.2.sub"), detail: tt("variant.c01.agenda.2.detail") },
    { time: "21:30", mins: 60, title: tt("variant.c01.agenda.3.title"), sub: tt("variant.c01.agenda.3.sub"), detail: tt("variant.c01.agenda.3.detail") },
    { time: "22:30", mins: 90, title: tt("variant.c01.agenda.4.title"), sub: tt("variant.c01.agenda.4.sub"), detail: tt("variant.c01.agenda.4.detail") },
  ];
  const agendaMaxMins = Math.max(...agendaRows.map((r) => r.mins));

  // Speakers
  const speakers: Array<{ name: string; role: string; file: string }> = [
    { name: "Aziz Karimov", role: tt("variant.c01.speakers.0.role"), file: "keynote.tsx" },
    { name: "Madina Saidova", role: tt("variant.c01.speakers.1.role"), file: "growth.tsx" },
    { name: "Bobur Yusupov", role: tt("variant.c01.speakers.2.role"), file: "platform.tsx" },
  ];

  // Permitted black-tie palette (visual only — strict monochrome ink).
  const blackTieSwatches: Array<{ label: string; fill: string; stroke?: string }> = [
    { label: tt("variant.c01.dress.swatch.0"), fill: "#1A1814" },
    { label: tt("variant.c01.dress.swatch.1"), fill: "#FFFFFF", stroke: t.ink },
    { label: tt("variant.c01.dress.swatch.2"), fill: "#F5EFE6", stroke: t.ink },
    { label: tt("variant.c01.dress.swatch.3"), fill: "#C9A75A" },
  ];

  return (
    <div className="forum-variant">
      {/* ───── Mobile-only refinements (≤768px) ──────────────────────────
          Scoped via .forum-variant. Desktop styles untouched. Lockdown:
          no motion. All overrides respect monochrome/mono typography. */}
      <style>{`
        .forum-variant .forum-mobile-only { display: none; }
        .forum-variant button:focus-visible,
        .forum-variant a:focus-visible {
          outline: 2px solid ${t.ink};
          outline-offset: 2px;
        }
        @media (max-width: 768px) {
          .forum-variant button,
          .forum-variant a[role="button"] { min-height: 44px; }
          .forum-variant .forum-hero h1 { font-size: clamp(48px, 14vw, 88px) !important; }
        }
        @media (max-width: 768px) {
          /* 7. Static dot-grid backdrop — silence scan animation */
          .forum-variant .forum-scan { display: none !important; animation: none !important; }

          /* 1. Hero masthead reformat — stack VOL/DATE strip as 2 mono lines */
          .forum-variant .forum-top-strip {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 6px !important;
            padding-bottom: 14px !important;
          }
          .forum-variant .forum-top-strip > div { font-size: 11px !important; letter-spacing: 0.28em !important; }
          .forum-variant .forum-top-strip .forum-vol { display: none !important; }

          /* Big names — clamp(48px, 16vw, 96px), tight line-height 0.95 */
          .forum-variant .forum-hero-name {
            font-size: clamp(48px, 16vw, 96px) !important;
            line-height: 0.95 !important;
            letter-spacing: -0.03em !important;
            font-feature-settings: "tnum" 1, "lnum" 1 !important;
          }
          .forum-variant .forum-hero-name::after { display: none !important; }

          /* 2. Live-typing subtitle — hide animated, show static final state */
          .forum-variant .forum-subtitle-animated { display: none !important; }
          .forum-variant .forum-mobile-only.forum-subtitle-static {
            display: block !important;
            color: ${t.ink}99;
          }
          .forum-variant .forum-subtitle-static .forum-caret-static {
            display: inline-block;
            margin-left: 4px;
            width: 0.55em;
            height: 1em;
            vertical-align: -0.18em;
            background: ${t.ink};
            opacity: 0.7;
            animation: none;
          }

          /* 3. Hero metadata grid — 2x2 on mobile */
          .forum-variant .forum-stat-strip {
            grid-template-columns: 1fr 1fr !important;
            gap: 18px !important;
            margin-top: 32px !important;
            padding-top: 18px !important;
          }
          .forum-variant .forum-stat-strip .forum-stat-key { font-size: 9px !important; }
          .forum-variant .forum-stat-strip .forum-stat-val {
            font-size: 16px !important;
            font-style: italic;
            font-weight: 500 !important;
          }

          /* Bottom ticker — keep static (lockdown) */
          .forum-variant .forum-ticker { animation: none !important; }

          /* 6. // 0N labels — slightly smaller (10px) on mobile */
          .forum-variant .forum-section-label { font-size: 10px !important; }
          .forum-variant .forum-hero-section-label { font-size: 10px !important; }

          /* 4. Agenda — stack each row as a card with vertical timeline */
          .forum-variant .forum-agenda-head { display: none !important; }
          .forum-variant .forum-agenda-list {
            position: relative;
            padding-left: 18px;
          }
          .forum-variant .forum-agenda-list::before {
            content: "";
            position: absolute;
            left: 6px;
            top: 8px;
            bottom: 8px;
            width: 1px;
            background: ${t.ink}33;
          }
          .forum-variant .forum-agenda-row {
            display: block !important;
            padding: 14px 0 !important;
            border-top: none !important;
            border-bottom: 1px solid ${t.ink}22 !important;
            position: relative;
          }
          .forum-variant .forum-agenda-row::before {
            content: "";
            position: absolute;
            left: -16px;
            top: 22px;
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${t.ink};
          }
          .forum-variant .forum-agenda-row .forum-agenda-time {
            float: right;
            font-size: 16px !important;
            font-feature-settings: "tnum" 1, "lnum" 1 !important;
            color: ${t.ink};
          }
          .forum-variant .forum-agenda-row .forum-agenda-title {
            font-size: 15px !important;
          }
          .forum-variant .forum-agenda-row .forum-agenda-sub {
            color: ${t.ink}88;
            font-size: 11px !important;
          }
          .forum-variant .forum-agenda-row .forum-agenda-bar { display: none !important; }
          .forum-variant .forum-agenda-row .forum-agenda-details.forum-mobile-only {
            display: block !important;
            margin-top: 8px;
            font-family: ${t.font};
            font-size: 11px;
            color: ${t.ink}88;
          }
          .forum-variant .forum-agenda-row .forum-agenda-details summary {
            cursor: pointer;
            list-style: none;
            letter-spacing: 0.18em;
            text-transform: uppercase;
          }
          .forum-variant .forum-agenda-row .forum-agenda-details summary::-webkit-details-marker { display: none; }
          .forum-variant .forum-agenda-row .forum-agenda-details[open] summary { color: ${t.ink}; }
          .forum-variant .forum-agenda-row .forum-agenda-details p {
            margin: 6px 0 0;
            line-height: 1.55;
            font-size: 12px;
            color: ${t.ink};
          }

          /* 5. Speaker terminal cards — 1 col vertical reel */
          .forum-variant .forum-speakers-grid {
            grid-template-columns: 1fr !important;
            gap: 18px !important;
          }
          .forum-variant .forum-speaker-card {
            min-height: 280px;
          }
          .forum-variant .forum-speaker-photo { aspect-ratio: 16 / 10 !important; }

          /* 8. Dress code — bow tie centered, swatches 2x2 */
          .forum-variant .forum-dress-grid {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
          .forum-variant .forum-dress-bowtie { max-width: 320px; margin: 0 auto; }
          .forum-variant .forum-swatches {
            display: grid !important;
            grid-template-columns: 1fr 1fr !important;
            gap: 18px !important;
          }
          .forum-variant .forum-swatches > div { flex-direction: row !important; gap: 12px !important; align-items: center !important; justify-content: flex-start !important; }

          /* 9. Premium tier indicator — show centered above footer credit */
          .forum-variant .forum-tier-corner { display: none !important; }
          .forum-variant .forum-tier-mobile { display: flex !important; }

          /* 10. RSVP confirm button — black, full-width, 56px tall, mono */
          .forum-variant button[type="submit"],
          .forum-variant .rsvp-section button[type="submit"] {
            width: 100% !important;
            min-height: 56px !important;
            font-family: ${t.font} !important;
            text-transform: uppercase !important;
            letter-spacing: 0.2em !important;
            background: ${t.ink} !important;
            color: ${t.bg} !important;
          }

          /* Hero & section padding tighter on phones */
          .forum-variant .forum-hero { padding: 22px 18px !important; }
          .forum-variant .forum-section { padding: 56px 18px !important; }
        }
      `}</style>

      {/* ───── HERO ─────────────────────────────────────────────────────── */}
      <section
        className="forum-hero"
        style={{
          minHeight: "100vh",
          padding: "clamp(28px, 5vw, 60px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <HeroBackdrop ink={t.ink} animate={!reduce} />

        {/* Top keyline — running ticker */}
        <div
          className="forum-top-strip"
          style={{
            position: "relative",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            borderBottom: `1px solid ${t.ink}`,
            paddingBottom: 16,
            gap: 24,
            flexWrap: "wrap",
            ...tnum,
          }}
        >
          <div style={{ fontFamily: t.font, fontSize: 12, fontWeight: 700, letterSpacing: "0.3em" }}>
            FORUM / {t.date.split(".")[2]}
          </div>
          <div className="forum-vol" style={{ fontFamily: t.font, fontSize: 12, fontWeight: 700, letterSpacing: "0.3em", opacity: 0.55 }}>
            {tt("variant.c01.hero.vol")}
          </div>
          <div style={{ fontFamily: t.font, fontSize: 12, fontWeight: 700, letterSpacing: "0.3em" }}>{t.date}</div>
        </div>

        {/* Center: huge tabular hero */}
        <div style={{ position: "relative", marginTop: 48, marginBottom: 48 }}>
          <div
            className="forum-hero-section-label"
            style={{
              fontFamily: t.font,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.3em",
              color: t.ink,
              marginBottom: 24,
              ...tnum,
            }}
          >
            {tt("variant.c01.hero.label")}
          </div>

          {/* Hero name — DM Mono, huge, with a hairline that draws on hover */}
          <h1
            className="forum-hero-name"
            style={{
              fontFamily: t.font,
              fontSize: "clamp(64px, 13vw, 220px)",
              fontWeight: 700,
              lineHeight: 0.92,
              letterSpacing: "-0.04em",
              margin: 0,
              ...tnum,
            }}
          >
            <span style={{ display: "inline-block" }}>{nameLeft}</span>
            <br />
            <span style={{ display: "inline-block", opacity: 0.55 }}>&amp;</span>{" "}
            <span style={{ display: "inline-block" }}>{nameRight}</span>
          </h1>
          {/* Hairline that draws across on hover (CSS only) */}
          <style>{`
            .forum-hero-name {
              position: relative;
            }
            .forum-hero-name::after {
              content: "";
              position: absolute;
              left: 0;
              bottom: -10px;
              height: 1px;
              width: 0%;
              background: ${t.ink};
              transition: width 600ms cubic-bezier(.2,.7,.2,1);
            }
            .forum-hero-name:hover::after,
            section:hover > div .forum-hero-name::after {
              width: 100%;
            }
            @media (prefers-reduced-motion: reduce) {
              .forum-hero-name::after { transition: none; width: 100%; }
            }
          `}</style>

          {/* Live-typing subtitle (signature element) — desktop only */}
          <div
            className="forum-subtitle-animated"
            aria-label={subtitleFull}
            style={{
              marginTop: 28,
              fontFamily: t.font,
              fontSize: "clamp(13px, 1.4vw, 18px)",
              fontWeight: 500,
              letterSpacing: "0.08em",
              color: t.ink,
              minHeight: "1.4em",
              ...tnum,
            }}
          >
            <span>{typed}</span>
            <span
              aria-hidden
              className="forum-caret"
              style={{
                display: "inline-block",
                marginLeft: 4,
                width: "0.55em",
                height: "1em",
                verticalAlign: "-0.18em",
                background: t.ink,
                animation: !reduce && !typedDone ? "forum-caret 1s steps(1) infinite" : reduce ? undefined : "forum-caret 1s steps(1) infinite",
                opacity: typedDone ? 0 : 1,
                transition: typedDone ? "opacity 220ms ease 200ms" : undefined,
              }}
            />
          </div>

          {/* Mobile-only static final-state subtitle (lockdown: no caret blink) */}
          <div
            className="forum-mobile-only forum-subtitle-static"
            style={{
              marginTop: 20,
              fontFamily: t.font,
              fontSize: 13,
              fontWeight: 500,
              letterSpacing: "0.06em",
              color: `${t.ink}99`,
              minHeight: "1.4em",
              ...tnum,
            }}
          >
            <span>{subtitleFull}</span>
            <span aria-hidden className="forum-caret-static" />
          </div>

          {/* Stat strip: DATE / TIME / VENUE / DRESS */}
          <div
            className="forum-stat-strip"
            style={{
              marginTop: 48,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: 24,
              paddingTop: 24,
              borderTop: `1px solid ${t.ink}`,
            }}
          >
            {(
              [
                [tt("variant.c01.hero.stat.date"), t.date],
                [tt("variant.c01.hero.stat.time"), "19:00"],
                [tt("variant.c01.hero.stat.venue"), t.venue.toUpperCase()],
                [tt("variant.c01.hero.stat.dress"), tt("variant.c01.hero.stat.dress_value")],
              ] as const
            ).map(([k, v]) => (
              <div key={k}>
                <div
                  className="forum-stat-key"
                  style={{
                    fontFamily: t.font,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.32em",
                    color: `${t.ink}88`,
                    marginBottom: 10,
                    ...tnum,
                  }}
                >
                  {k}
                </div>
                <div
                  className="forum-stat-val"
                  style={{
                    fontFamily: t.font,
                    fontSize: 22,
                    fontWeight: 700,
                    letterSpacing: "-0.01em",
                    ...tnum,
                  }}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom ticker rail */}
        <div
          aria-hidden
          style={{
            position: "relative",
            borderTop: `1px solid ${t.ink}`,
            paddingTop: 14,
            overflow: "hidden",
            fontFamily: t.font,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: `${t.ink}aa`,
            ...tnum,
          }}
        >
          <div
            className="forum-ticker"
            style={{
              whiteSpace: "nowrap",
              display: "inline-block",
              animation: !reduce ? "forum-ticker 60s linear infinite" : undefined,
            }}
          >
            {Array.from({ length: 2 }).map((_, k) => (
              <span key={k}>
                {[
                  tt("variant.c01.ticker.0"),
                  `EST. ${t.date.split(".")[2]}`,
                  tt("variant.c01.ticker.1"),
                  tt("variant.c01.ticker.2"),
                  tt("variant.c01.ticker.3"),
                  tt("variant.c01.ticker.4"),
                  tt("variant.c01.ticker.5"),
                  tt("variant.c01.ticker.6"),
                ].map((s) => (
                  <span key={s} style={{ marginRight: 48 }}>
                    {s} <span style={{ opacity: 0.4 }}>·</span>
                  </span>
                ))}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ───── // 02 — AGENDA (real timetable) ──────────────────────────── */}
      <Reveal reduce={reduce} as="section" className="forum-section" style={{ padding: "clamp(72px, 10vw, 120px) clamp(28px, 5vw, 60px)", borderTop: `2px solid ${t.ink}` }}>
        <SectionLabel n="02" label={tt("variant.c01.agenda.label")} ink={t.ink} font={t.font} />
        <div className="forum-agenda-list" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 0 }}>
          {/* Header row */}
          <div
            className="forum-agenda-head"
            style={{
              display: "grid",
              gridTemplateColumns: "84px 1fr minmax(120px, 220px)",
              gap: 24,
              paddingBottom: 14,
              borderBottom: `1px solid ${t.ink}`,
              fontFamily: t.font,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.32em",
              color: `${t.ink}88`,
              ...tnum,
            }}
          >
            <span>{tt("variant.c01.agenda.head_time")}</span>
            <span>{tt("variant.c01.agenda.head_session")}</span>
            <span>{tt("variant.c01.agenda.head_duration")}</span>
          </div>

          {/* Rows */}
          {agendaRows.map((row, i) => {
            const widthPct = Math.max(20, Math.round((row.mins / agendaMaxMins) * 100));
            return (
              <AgendaRow
                key={row.time}
                row={row}
                widthPct={widthPct}
                t={t}
                tnum={tnum}
                reduce={reduce}
                onHover={() => play("tap")}
                isFirst={i === 0}
                minSuffix={tt("variant.c01.agenda.min_suffix")}
                detailsLabel={tt("variant.c01.agenda.details")}
              />
            );
          })}
        </div>
      </Reveal>

      {/* ───── // 03 — SPEAKERS (terminal cards) ────────────────────────── */}
      <Reveal reduce={reduce} as="section" className="forum-section" style={{ padding: "clamp(72px, 10vw, 120px) clamp(28px, 5vw, 60px)", borderTop: `2px solid ${t.ink}`, background: `${t.ink}06` }}>
        <SectionLabel n="03" label={tt("variant.c01.speakers.label")} ink={t.ink} font={t.font} />
        <div
          className="forum-speakers-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 24,
          }}
        >
          {speakers.map((sp, i) => (
            <SpeakerTerminal
              key={sp.name}
              speaker={sp}
              photo={t.photos[i] || t.photos[0]}
              t={t}
              reduce={reduce}
              onHover={() => play("tap")}
            />
          ))}
        </div>
      </Reveal>

      {/* ───── // 04 — DRESS CODE (BLACK TIE) ──────────────────────────── */}
      <Reveal reduce={reduce} as="section" className="forum-section" style={{ padding: "clamp(72px, 10vw, 120px) clamp(28px, 5vw, 60px)", borderTop: `2px solid ${t.ink}` }}>
        <SectionLabel n="04" label={tt("variant.c01.dress.label")} ink={t.ink} font={t.font} />
        <div
          className="forum-dress-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)",
            gap: "clamp(28px, 5vw, 64px)",
            alignItems: "center",
          }}
        >
          {/* Bow tie SVG */}
          <div
            className="forum-dress-bowtie"
            style={{
              border: `1px solid ${t.ink}`,
              padding: "clamp(20px, 4vw, 40px)",
              background: t.bg,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
            }}
          >
            <svg
              viewBox="0 0 200 96"
              width="100%"
              style={{ maxWidth: 220 }}
              aria-hidden
            >
              {/* knot */}
              <rect x="92" y="32" width="16" height="32" fill={t.ink} />
              {/* left wing */}
              <path d="M92 32 L 32 14 Q 18 22 18 48 Q 18 74 32 82 L 92 64 Z" fill={t.ink} />
              {/* right wing */}
              <path d="M108 32 L 168 14 Q 182 22 182 48 Q 182 74 168 82 L 108 64 Z" fill={t.ink} />
              {/* knot detail */}
              <line x1="100" y1="32" x2="100" y2="64" stroke={t.bg} strokeWidth="1.5" />
            </svg>
            <div
              style={{
                fontFamily: t.font,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.35em",
                ...tnum,
              }}
            >
              {tt("variant.c01.dress.title")}
            </div>
          </div>

          {/* Copy + permitted swatches */}
          <div>
            <p
              style={{
                fontFamily: t.font,
                fontSize: "clamp(15px, 1.5vw, 18px)",
                lineHeight: 1.6,
                color: t.ink,
                margin: 0,
                marginBottom: 28,
                maxWidth: 56 + "ch",
              }}
            >
              {tt("variant.c01.dress.body")}
            </p>

            <div
              style={{
                fontFamily: t.font,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.32em",
                color: `${t.ink}88`,
                marginBottom: 16,
                ...tnum,
              }}
            >
              {tt("variant.c01.dress.palette_label")}
            </div>
            <div className="forum-swatches" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {blackTieSwatches.map((sw) => (
                <div key={sw.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: "50%",
                      background: sw.fill,
                      border: `1px solid ${sw.stroke || t.ink}`,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: t.font,
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.24em",
                      color: t.ink,
                      ...tnum,
                    }}
                  >
                    {sw.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Reveal>

      {/* ───── // 05 — VENUE / MAP ─────────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ padding: "0 0", borderTop: `2px solid ${t.ink}` }}>
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(28px, 5vw, 60px) 0" }}>
            <SectionLabel n="05" label={tt("variant.c01.venue.label")} ink={t.ink} font={t.font} />
          </div>
          <MapSection t={t} isDark={isDark} addressLine={`${t.venue} · ${tt("variant.c01.map.address")}`} />
        </div>
      </Reveal>

      {/* ───── // 06 — RSVP ────────────────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ borderTop: `2px solid ${t.ink}` }}>
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(28px, 5vw, 60px) 0" }}>
            <SectionLabel n="06" label={tt("variant.c01.rsvp.label")} ink={t.ink} font={t.font} />
          </div>
          <div
            // Wire RSVP submit → tap (B2B sound).
            // We attach via mousedown on Yes/No buttons through a click capture
            // on the section root. Simpler than rebuilding RsvpSection.
            onClickCapture={(e) => {
              const target = e.target as HTMLElement;
              if (target && target.tagName === "BUTTON") play("tap");
            }}
          >
            <RsvpSection t={t} isDark={isDark} {...ctx.rsvp} label={tt("variant.c01.rsvp.confirm")} />
          </div>
        </div>
      </Reveal>

      {/* ───── // 07 — SHARE ──────────────────────────────────────────── */}
      <Reveal reduce={reduce} as="div">
        <div style={{ borderTop: `2px solid ${t.ink}` }}>
          <div style={{ padding: "clamp(48px, 8vw, 96px) clamp(28px, 5vw, 60px) 0" }}>
            <SectionLabel n="07" label={tt("variant.c01.share.label")} ink={t.ink} font={t.font} />
          </div>
          <div
            onClickCapture={(e) => {
              const target = e.target as HTMLElement;
              if (target && target.closest("button")) play("click");
            }}
          >
            <ShareSection t={t} />
          </div>
        </div>
      </Reveal>

      {/* ───── FOOTER + tier watermark ─────────────────────────────────── */}
      <div style={{ position: "relative" }}>
        {/* Mobile-only: centered tier indicator above MADE WITH TAKLIFLA.UZ */}
        <div
          aria-hidden
          className="forum-mobile-only forum-tier-mobile"
          style={{
            display: "none",
            justifyContent: "center",
            alignItems: "center",
            gap: 10,
            padding: "18px 16px 6px",
            fontFamily: t.font,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: `${t.ink}88`,
            ...tnum,
          }}
        >
          <span>{tt("variant.c01.tier")}</span>
          <span style={{ letterSpacing: "0.4em" }}>★ ★ ★</span>
        </div>
        <GuestFooter t={t} />
        <div
          aria-hidden
          className="forum-tier-corner"
          style={{
            position: "absolute",
            right: 16,
            bottom: 14,
            fontFamily: t.font,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: `${t.ink}66`,
            display: "flex",
            alignItems: "center",
            gap: 8,
            ...tnum,
          }}
        >
          <span>{tt("variant.c01.tier")}</span>
          <span style={{ letterSpacing: "0.4em" }}>★ ★ ★</span>
        </div>
      </div>
    </div>
  );
}

// ─── AgendaRow: row with time, title, sub, duration bar ─────────────────────
function AgendaRow({
  row,
  widthPct,
  t,
  tnum,
  reduce,
  onHover,
  isFirst,
  minSuffix,
  detailsLabel,
}: {
  row: { time: string; mins: number; title: string; sub: string; detail: string };
  widthPct: number;
  t: VariantProps["t"];
  tnum: CSSProperties;
  reduce: boolean;
  onHover: () => void;
  isFirst: boolean;
  minSuffix: string;
  detailsLabel: string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      className="forum-agenda-row"
      onMouseEnter={() => {
        setHover(true);
        onHover();
      }}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid",
        gridTemplateColumns: "84px 1fr minmax(120px, 220px)",
        gap: 24,
        padding: "22px 0",
        borderTop: isFirst ? "none" : `1px solid ${t.ink}22`,
        alignItems: "center",
        position: "relative",
        background: hover && !reduce ? `${t.ink}06` : "transparent",
        transition: reduce ? "none" : "background 160ms ease",
      }}
    >
      <span
        className="forum-agenda-time"
        style={{
          fontFamily: t.font,
          fontSize: "clamp(20px, 2.4vw, 28px)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          ...tnum,
        }}
      >
        {row.time}
      </span>
      <div>
        <div
          className="forum-agenda-title"
          style={{
            fontFamily: t.font,
            fontSize: "clamp(16px, 1.6vw, 20px)",
            fontWeight: 700,
            letterSpacing: "-0.01em",
            marginBottom: 4,
          }}
        >
          {row.title}
        </div>
        <div
          className="forum-agenda-sub"
          style={{
            fontFamily: t.font,
            fontSize: 12,
            color: `${t.ink}99`,
            letterSpacing: "0.04em",
          }}
        >
          {row.sub}
        </div>
        {/* Mobile-only: tap-to-expand description (no JS, native <details>) */}
        <details className="forum-mobile-only forum-agenda-details">
          <summary>{detailsLabel}</summary>
          <p>{row.detail}</p>
        </details>
      </div>
      <div className="forum-agenda-bar">
        <div
          aria-hidden
          style={{
            position: "relative",
            height: 6,
            background: `${t.ink}11`,
            overflow: "hidden",
          }}
        >
          <div
            className="forum-bar"
            style={{
              position: "absolute",
              inset: 0,
              width: `${widthPct}%`,
              background: t.ink,
              transformOrigin: "left center",
              animation: reduce ? undefined : "forum-bar 600ms cubic-bezier(.2,.7,.2,1) both",
            }}
          />
        </div>
        <div
          style={{
            marginTop: 8,
            fontFamily: t.font,
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.28em",
            color: `${t.ink}88`,
            ...tnum,
          }}
        >
          {row.mins} {minSuffix}
        </div>
      </div>
    </div>
  );
}

// ─── SpeakerTerminal: IDE-window-styled speaker card ────────────────────────
function SpeakerTerminal({
  speaker,
  photo,
  t,
  reduce,
  onHover,
}: {
  speaker: { name: string; role: string; file: string };
  photo: string | undefined;
  t: VariantProps["t"];
  reduce: boolean;
  onHover: () => void;
}) {
  const [hover, setHover] = useState(false);
  const tnum: CSSProperties = { fontFeatureSettings: '"tnum" 1, "lnum" 1' };
  return (
    <div
      className="forum-speaker-card"
      onMouseEnter={() => {
        setHover(true);
        onHover();
      }}
      onMouseLeave={() => setHover(false)}
      style={{
        border: `1px solid ${t.ink}`,
        background: t.bg,
        display: "flex",
        flexDirection: "column",
        transition: reduce ? "none" : "transform 200ms ease",
        transform: hover && !reduce ? "translateY(-2px)" : "translateY(0)",
      }}
    >
      {/* terminal title bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 14px",
          borderBottom: `1px solid ${t.ink}`,
          background: `${t.ink}08`,
        }}
      >
        <span style={{ display: "inline-flex", gap: 6 }}>
          {[0, 1, 2].map((d) => (
            <span
              key={d}
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                border: `1px solid ${t.ink}`,
                background: "transparent",
              }}
            />
          ))}
        </span>
        <span
          style={{
            fontFamily: t.font,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.12em",
            color: `${t.ink}aa`,
            ...tnum,
          }}
        >
          ~/speakers/{speaker.file}
        </span>
      </div>

      {/* preview pane: photo */}
      <div
        className="forum-speaker-photo"
        style={{
          aspectRatio: "1 / 1",
          background: `${t.ink}11`,
          overflow: "hidden",
          position: "relative",
          borderBottom: `1px solid ${t.ink}`,
        }}
      >
        <GuestPhoto
          src={photo}
          style={{
            width: "100%",
            height: "100%",
            transition: reduce ? "none" : "filter 220ms ease, transform 600ms ease",
          }}
          filter={hover && !reduce ? "grayscale(1) contrast(1.05) brightness(0.92)" : "grayscale(1) contrast(1.02)"}
          fallbackBg={t.bg}
          fallbackTone={t.accent}
        />
        {/* scanline overlay */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            background: `repeating-linear-gradient(0deg, transparent 0 3px, ${t.ink}06 3px 4px)`,
            pointerEvents: "none",
            opacity: hover ? 0.9 : 0.6,
            transition: reduce ? "none" : "opacity 200ms ease",
          }}
        />
      </div>

      {/* "code" body */}
      <div style={{ padding: "16px 16px 18px" }}>
        <div
          style={{
            fontFamily: t.font,
            fontSize: 11,
            color: `${t.ink}88`,
            marginBottom: 6,
            ...tnum,
          }}
        >
          <span style={{ color: `${t.ink}66` }}>const </span>
          <span style={{ fontWeight: 700, color: t.ink }}>speaker</span>
          <span> = {"{"}</span>
        </div>
        <div
          style={{
            fontFamily: t.font,
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: "-0.01em",
            color: t.ink,
            paddingLeft: 16,
            marginBottom: 4,
          }}
        >
          name: <span style={{ fontWeight: 500 }}>"{speaker.name}"</span>,
        </div>
        <div
          style={{
            fontFamily: t.font,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.18em",
            paddingLeft: 16,
            // syntax-highlight effect — underline for accent in monochrome.
            textDecoration: "underline",
            textDecorationThickness: "1px",
            textUnderlineOffset: "3px",
            color: t.ink,
            ...tnum,
          }}
        >
          role: <span style={{ letterSpacing: "0.28em" }}>"{speaker.role}"</span>
        </div>
        <div
          style={{
            fontFamily: t.font,
            fontSize: 11,
            color: `${t.ink}88`,
            marginTop: 6,
            ...tnum,
          }}
        >
          {"}"};
        </div>
      </div>
    </div>
  );
}
